import type { CostCell, CostDraft } from './costDraft';
import type { ColumnMapping } from './columnMapping';

export interface CellError { ref: string; quarter: string; raw: string; }

/** Minimal RFC4180 split: handles quoted fields containing commas. */
function splitRow(line: string): string[] {
  const out: string[] = [];
  let cur = '', inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i]!;
    if (c === '"') {
      if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; } else inQuotes = !inQuotes;
    } else if (c === ',' && !inQuotes) { out.push(cur); cur = ''; } else cur += c;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

/** Whole-file split: BOM stripped, blank lines dropped, every cell trimmed.
 *  The ONE CSV parser — costWorksheet.ts and ImportPage go through this too,
 *  so a quoting quirk cannot be handled two different ways. */
export function parseCsvRows(text: string): string[][] {
  const clean = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  return clean.split(/\r?\n/).filter((l) => l.trim() !== '').map(splitRow);
}

/** '$1,234.56' -> '1234.56'. Strips currency symbols, thousands separators and
 *  whitespace; leaves the decimal alone. Null when it is not a number — NEVER 0.
 *
 *  Does NOT scale to µ$ or cents: that depends on whether the row is a component
 *  or a product, and this module has never seen the catalog. planWrites scales,
 *  once matchDraft has classified the ref. Scaling here would mean inferring the
 *  type from the SKU string, which is the VCH-FRU-* trap. */
export function normalizeAmount(raw: string): string | null {
  const cleaned = raw.replace(/[$,\s]/g, '');
  if (!/^-?\d+(\.\d{1,6})?$/.test(cleaned)) return null;
  return cleaned;
}

export function readSheet(
  text: string, mapping: ColumnMapping, vendor: string, sourceLabel: string,
): { draft: CostDraft; ignoredColumns: string[]; errors: CellError[] } {
  const rows = parseCsvRows(text);
  const headers = rows[0] ?? [];

  // Detect duplicate headers. A duplicated ref or quarter column is silent data loss;
  // a duplicated ignored column is harmless. Throw if a mapped column is duplicated.
  const headerIndices = new Map<string, number[]>();
  for (let i = 0; i < headers.length; i++) {
    const h = headers[i]!;
    if (!headerIndices.has(h)) {
      headerIndices.set(h, []);
    }
    headerIndices.get(h)!.push(i);
  }
  for (const [h, indices] of headerIndices.entries()) {
    if (h === '') continue;   // a blank header can never be a mapped column
    if (indices.length > 1 && (h === mapping.refColumn || h in mapping.quarterColumns)) {
      throw new Error(`Duplicate column "${h}" at indices ${indices.join(', ')}`);
    }
  }

  const index = new Map(headers.map((h, i) => [h, i]));

  const ignoredColumns = headers.filter(
    (h) => (h === '' || (h !== mapping.refColumn && !(h in mapping.quarterColumns))),
  );

  const cells: CostCell[] = [];
  const errors: CellError[] = [];

  for (const values of rows.slice(1)) {
    const ref = values[index.get(mapping.refColumn) ?? 0] ?? '';
    if (!ref) continue;

    for (const [header, quarter] of Object.entries(mapping.quarterColumns)) {
      const raw = values[index.get(header)!] ?? '';
      if (raw === '') continue;                       // absent, not zero
      const amount = normalizeAmount(raw);
      if (amount === null) { errors.push({ ref, quarter, raw }); continue; }
      cells.push({ ref, target: null, quarter, amount });
    }
  }
  return { draft: { vendor, cells, sourceLabel }, ignoredColumns, errors };
}
